import React, { useState } from 'react';
import { Box, Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatSectionValue, MedalKey, MEDALS, MedalSection, PLATINUM_START, SECTION_HELP, TIER_OUTCOMES, TierHistoryPoint } from './scorecardData';
import { NOT_REQUIRED } from '../../utils/requirementsScore';
import ElliottFace from '../../pages/new-designs/components/ElliottFace';
import { useAppSelector } from '../../redux/hooks';
import { getUserDetails } from '../../utils/orgName';

// Medal popup — Cian feedback v2, replaces the Readiness/Dreyfus framing.
// Five sections, each rendered as a banded progress bar: medal tiers
// (Rust / Bronze / Silver / Gold) bracketed along the score row, with a
// marker showing where the learner currently sits. Overall medal = the
// LOWEST medal held across all sections. Platinum is relative: top 10% of
// members drawn only from the pool already at Gold in that section — no
// Gold pool, no Platinum (stops new orgs getting Platinum by default).
// Open decisions (overall calc, band rounding, s5 window, retakes, etc.)
// are tracked in docs/EPIC-gamification.md.

const SURFACE = '#1C1C1C';
const BORDER_DIM = '#2A2A2A';
const TEXT_PRIMARY = '#FDFDFD';
const TEXT_SECONDARY = '#B6B6B6';
const TEXT_TERTIARY = '#868686';
const TRACK = '#2A2A2A';
const BRAND_LIGHT = '#F79420';
// A measure with no medal (nothing required of them, DA-5056 R5): the badge in grey.
const NO_MEDAL = '#565656';

// Who is reading the panel. The bars are identical; the framing copy and the
// predicted-outcomes table are what differ.
export type Audience = 'learner' | 'orgAdmin' | 'groupAdmin';

// Every tier badge is a rectangle — the shape is the tier's signature, used
// by the bar chip, the panel's headline badge and each section's tier label.
const TIER_RADIUS = '4px';

// Headline tier badge — same shape language as the bar chip, sized down to sit
// beside the eyebrow title without dwarfing it.
const TierBadge: React.FC<{ medalKey: MedalKey }> = ({ medalKey }) => {
  const m = MEDALS[medalKey];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        height: 24,
        px: '11px',
        borderRadius: TIER_RADIUS,
        // `${hex}1F` / `${hex}55` = the tier colour at ~12% / ~33% alpha.
        bgcolor: `${m.color}1F`,
        border: `1px solid ${m.color}`,
        boxShadow: `0 0 10px ${m.color}55`,
        color: m.color,
        fontFamily: 'Inter, sans-serif',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '1px',
        textTransform: 'uppercase'
      }}
    >
      <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: m.color }} />
      {m.label}
    </Box>
  );
};

// "How To Improve" — replaces the measure line on each section row. Opens on
// hover and toggles on click, so it works with a mouse or a tap.
const HowToImprove: React.FC<{ sectionName: string; isAdmin: boolean }> = ({ sectionName, isAdmin }) => {
  const [open, setOpen] = useState(false);
  const help = SECTION_HELP[sectionName]?.[isAdmin ? 'admin' : 'learner'];
  if (!help) return null;
  return (
    <Tooltip
      open={open}
      onClose={() => setOpen(false)}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      placement="top-end"
      title={
        <Box sx={{ p: '4px 2px', maxWidth: 320 }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 11.5, color: TEXT_SECONDARY, mb: '8px' }}>
            <Box component="span" sx={{ color: BRAND_LIGHT, fontWeight: 700 }}>
              Definition:{' '}
            </Box>
            {help.definition}
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 11.5, color: TEXT_SECONDARY }}>
            <Box component="span" sx={{ color: BRAND_LIGHT, fontWeight: 700 }}>
              How To Improve:{' '}
            </Box>
            {help.howToImprove}
          </Typography>
        </Box>
      }
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: '#232323',
            border: `1px solid ${BORDER_DIM}`,
            borderRadius: '8px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
            p: '10px 12px'
          }
        }
      }}
    >
      <Box
        component="span"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
          cursor: 'pointer',
          fontFamily: 'Inter, sans-serif',
          fontSize: 11,
          fontWeight: 600,
          color: BRAND_LIGHT,
          whiteSpace: 'nowrap',
          transition: 'filter 0.15s ease',
          '&:hover': { filter: 'brightness(1.15)' }
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 13 }} />
        How To Improve
      </Box>
    </Tooltip>
  );
};

// One section: name + value/tier on top, then the banded bar. The track is
// split into the four tier bands (tinted), boundary ticks mark the brackets,
// and the ▼ marker shows the learner's position.
const SectionBar: React.FC<{ section: MedalSection; showPlatinum: boolean; isAdmin: boolean }> = ({
  section,
  showPlatinum,
  isAdmin
}) => {
  const [b1, b2, b3] = section.boundaries;
  // Platinum takes the final stretch of the track on the learner side; the
  // admin panel stops at Gold, which then runs to the end.
  const bands: Array<{ from: number; to: number; key: MedalKey }> = [
    { from: 0, to: b1, key: 'wood' },
    { from: b1, to: b2, key: 'bronze' },
    { from: b2, to: b3, key: 'silver' },
    { from: b3, to: showPlatinum ? PLATINUM_START : 100, key: 'gold' },
    ...(showPlatinum ? [{ from: PLATINUM_START, to: 100, key: 'platinum' as MedalKey }] : [])
  ];
  const gates = showPlatinum ? [b1, b2, b3, PLATINUM_START] : [b1, b2, b3];
  const activeBand = bands.findIndex((band, i) =>
    i === bands.length - 1 ? section.pos >= band.from : section.pos >= band.from && section.pos < band.to
  );
  // The label follows the band the marker actually sits in, so a learner in
  // the Platinum stretch reads PLATINUM rather than the payload's GOLD. No
  // medal: nothing it measures is required of them (DA-5056 R5), so the badge
  // reads "-" in grey and there is no marker.
  const medalKey: MedalKey | null =
    section.medal === null ? null : activeBand >= 0 ? bands[activeBand].key : section.medal;
  const m = medalKey ? MEDALS[medalKey] : { color: NO_MEDAL, label: NOT_REQUIRED };
  return (
    <Box
      sx={{
        mb: '10px',
        p: '12px 14px',
        borderRadius: '8px',
        border: `1px solid ${BORDER_DIM}`
      }}
    >
      {/* Fixed-width columns so the tier badges and figures line up down the
          panel: name, then tier, then the section's own figure. The measure
          line is gone — its detail lives behind How To Improve on the right. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Typography
          sx={{
            width: 152,
            flexShrink: 0,
            fontFamily: 'Inter, sans-serif',
            fontSize: 13,
            fontWeight: 600,
            color: TEXT_PRIMARY
          }}
        >
          {section.name}
        </Typography>
        {/* Outlined rectangle, same badge language as the headline tier. */}
        <Box sx={{ width: 78, flexShrink: 0, display: 'flex', justifyContent: 'flex-start' }}>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 68,
              px: '8px',
              py: '4px',
              borderRadius: TIER_RADIUS,
              border: `1px solid ${m.color}`,
              bgcolor: `${m.color}1F`,
              color: m.color,
              fontFamily: 'Inter, sans-serif',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.5px',
              lineHeight: 1
            }}
          >
            {m.label.toUpperCase()}
          </Box>
        </Box>
        <Typography
          sx={{
            width: 96,
            flexShrink: 0,
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            fontWeight: 600,
            color: TEXT_SECONDARY
          }}
        >
          {/* Same shortening as the tier panel, so the two never disagree. */}
          {formatSectionValue(section.name, section.value).text}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
          <HowToImprove sectionName={section.name} isAdmin={isAdmin} />
        </Box>
      </Box>

      {/* Banded track: every tier at its true colour (no shading — the bands
          are told apart by hue, not brightness), boundary ticks, and the
          marker showing where the learner sits. Bottom padding leaves room
          for the gate labels beneath. */}
      <Box sx={{ position: 'relative', mt: '10px', pb: '16px' }}>
        <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', bgcolor: TRACK }}>
          {bands.map((band) => (
            <Box key={band.key} sx={{ width: `${band.to - band.from}%`, bgcolor: MEDALS[band.key].color }} />
          ))}
        </Box>
        {gates.map((b) => (
          <Box
            key={b}
            sx={{ position: 'absolute', top: -2, height: 12, left: `${b}%`, width: '2px', bgcolor: '#0F0F0F' }}
          />
        ))}
        {/* Gate labels — the value at each tier boundary, so the learner can
            see what it takes to cross into the next band. Percentages by
            default; the section's own units when it supplies gateLabels. */}
        {gates.map((b, gateIndex) => (
          <Typography
            key={`gate-${b}`}
            sx={{
              position: 'absolute',
              top: 12,
              left: `${b}%`,
              transform: 'translateX(-50%)',
              fontFamily: 'Inter, sans-serif',
              fontSize: 9.5,
              color: TEXT_TERTIARY,
              whiteSpace: 'nowrap'
            }}
          >
            {section.gateLabels?.[gateIndex] ?? `${Math.round(b)}%`}
          </Typography>
        ))}
        {/* Marker — brandy, so it never blends into the white section title
            or into the band it happens to be sitting on. Hovering it reveals
            the exact percentage this measure sits at on the bar. None for a
            measure nothing is required of. */}
        {medalKey && (
          <Tooltip title={`${Math.round(section.pos)}%`} arrow placement="top">
            <Box
              sx={{
                position: 'absolute',
                top: -8,
                left: `${Math.min(section.pos, 99)}%`,
                transform: 'translateX(-50%)',
                width: 16,
                height: 14,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                cursor: 'pointer'
              }}
            >
              <Box
                sx={{
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: `8px solid ${BRAND_LIGHT}`,
                  filter: `drop-shadow(0 0 4px ${BRAND_LIGHT})`
                }}
              />
            </Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export interface MedalsData {
  overall: MedalKey;
  sections: MedalSection[];
  /** Weekly tier snapshots for the 6 month view. Absent until the BE ships them. */
  history?: TierHistoryPoint[];
}

// Admin framing: the same five measures, read across a population rather than
// one person. Org Admins see the whole organization; Program/Group Admins see
// only the learners in the programs they administer.
// Lives behind the info icon beside the title.
export const adminIntro = (_orgName?: string): string[] => [
  'Your Learning Tier measures how your teams are developing through learning.'
];

// Pulled out of the intro into its own footer note — it's the one line that
// tells the admin what to actually do.
const WEAKEST_LINK_NOTE =
  'Note that your overall tier is only as strong as your weakest, so proactive encouragement across all measures is required to improve your tier.';

// Platinum is a learner-level distinction (top 10% of the Gold pool), so the
// admin panel stops at Gold.
const LEARNER_TIERS: MedalKey[] = ['wood', 'bronze', 'silver', 'gold', 'platinum'];
export const ADMIN_TIERS: MedalKey[] = ['wood', 'bronze', 'silver', 'gold'];

// Predicted outcome profile per tier — admin panels only; the learner panel
// keeps its own footer instead.
export const OutcomesTable: React.FC<{ overall: MedalKey; tiers: MedalKey[] }> = ({ overall, tiers }) => (
  <>
    <Typography
      sx={{
        fontFamily: 'Inter, sans-serif',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '1.2px',
        color: BRAND_LIGHT,
        mt: '18px',
        mb: '8px'
      }}
    >
      PREDICTED OUTCOMES BY TIER{' '}
      <Box component="span" sx={{ fontStyle: 'italic', fontWeight: 400, letterSpacing: 0, color: TEXT_TERTIARY }}>
        (Bersin HILO)
      </Box>
    </Typography>
    <Box sx={{ border: `1px solid ${BORDER_DIM}`, borderRadius: '8px', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', bgcolor: '#151515', px: '12px', py: '6px' }}>
        <Typography sx={{ flex: 1.4 }} />
        {tiers.map((k) => (
          // The current tier is outlined rather than underlined, matching the
          // tier badges everywhere else in the panel.
          <Box key={k} sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Box
              component="span"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 11,
                fontWeight: 700,
                lineHeight: 1,
                color: MEDALS[k].color,
                px: '7px',
                py: '4px',
                borderRadius: TIER_RADIUS,
                border: `1px solid ${k === overall ? MEDALS[k].color : 'transparent'}`,
                bgcolor: k === overall ? `${MEDALS[k].color}1F` : 'transparent'
              }}
            >
              {MEDALS[k].label}
            </Box>
          </Box>
        ))}
      </Box>
      {TIER_OUTCOMES.map((row, i) => (
        <Box
          key={row.metric}
          sx={{ display: 'flex', px: '12px', py: '7px', borderTop: i === 0 ? 'none' : `1px solid ${BORDER_DIM}` }}
        >
          <Typography sx={{ flex: 1.4, fontFamily: 'Inter, sans-serif', fontSize: 12, color: TEXT_SECONDARY }}>
            {row.metric}
          </Typography>
          {tiers.map((k) => (
            <Typography
              key={k}
              sx={{
                flex: 1,
                fontFamily: 'Inter, sans-serif',
                fontSize: 12,
                color: k === overall ? TEXT_PRIMARY : TEXT_TERTIARY,
                fontWeight: k === overall ? 600 : 400,
                textAlign: 'right'
              }}
            >
              {row[k]}
            </Typography>
          ))}
        </Box>
      ))}
    </Box>
  </>
);

const MedalsPanel: React.FC<{
  open: boolean;
  onClose: () => void;
  data: MedalsData;
  orgName: string;
  audience: Audience;
}> = ({ open, onClose, data, orgName, audience }) => {
  const isAdmin = audience !== 'learner';
  // Platinum is a learner-level distinction, so the admin panel stops at Gold.
  const tiers = isAdmin ? ADMIN_TIERS : LEARNER_TIERS;
  return (
    // A real modal: dead-centre on screen, blurred backdrop, X to close.
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      BackdropProps={{
        sx: { bgcolor: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }
      }}
      PaperProps={{
        sx: {
          position: 'relative',
          bgcolor: SURFACE,
          border: `1px solid ${BORDER_DIM}`,
          borderRadius: '12px',
          width: 900,
          maxWidth: 'calc(100vw - 48px)',
          maxHeight: 'calc(100vh - 64px)',
          // Flex column + hidden overflow so the body (below) can scroll while
          // the close button stays pinned — the modal is usable even on short
          // / minimized windows.
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="Close"
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 1,
          color: TEXT_TERTIARY,
          '&:hover': { color: TEXT_PRIMARY, bgcolor: 'rgba(255,255,255,0.06)' }
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>

      {/* Scrollable body — keeps the whole breakdown reachable when the
          viewport is shorter than the content. */}
      <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0, p: '24px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Same brandy eyebrow as the Streak / Top Performers / Programs Due
            panels, with the tier dot standing in for their icons. */}
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontFamily: 'Inter, sans-serif',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '1.2px',
              color: BRAND_LIGHT,
              lineHeight: 1,
              whiteSpace: 'nowrap'
            }}
          >
            {/* Elliott stands in for the tier dot — the panel is his read on the
              org's development, so he fronts it. */}
            <ElliottFace size={20} glow={false} />
            {audience === 'orgAdmin' ? 'LEARNING TIER' : 'YOUR LEARNING TIER'}
          </Typography>
          {isAdmin && (
            <Tooltip
              placement="bottom-start"
              title={
                <Box sx={{ p: '4px 2px' }}>
                  {adminIntro(orgName).map((paragraph, i, all) => (
                    <Typography
                      key={paragraph.slice(0, 24)}
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 11.5,
                        lineHeight: 1.5,
                        color: TEXT_SECONDARY,
                        mb: i === all.length - 1 ? 0 : '8px'
                      }}
                    >
                      {paragraph}
                    </Typography>
                  ))}
                </Box>
              }
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#232323',
                    border: `1px solid ${BORDER_DIM}`,
                    borderRadius: '8px',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
                    p: '10px 12px',
                    maxWidth: 420
                  }
                }
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 15, color: BRAND_LIGHT, cursor: 'pointer', display: 'block' }} />
            </Tooltip>
          )}
          {/* Badge sits directly beside the title, not out at the far edge. */}
          <TierBadge medalKey={data.overall} />
        </Box>
        {/* Admin framing lives behind the info icon beside the title. */}
        {!isAdmin && (
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: TEXT_TERTIARY, mt: '8px' }}>
            {`Your Learning Tier is the lowest tier you hold across your 5 measures of development. Your Learning Tier is used by management at ${
              orgName || 'your organization'
            } to easily track who is developing themselves through learning. See below how to improve your tier across each measure.`}
          </Typography>
        )}

        {/* Tier legend — brackets shown once, apply to every bar below. */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: '14px', mt: '16px', mb: '16px', flexWrap: 'wrap' }}>
          {tiers.map((k) => (
            <Box key={k} sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: MEDALS[k].color }} />
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 11.5, color: TEXT_SECONDARY }}>
                {MEDALS[k].label}
              </Typography>
            </Box>
          ))}
        </Box>

        {data.sections.map((section) => (
          <SectionBar key={section.name} section={section} showPlatinum={!isAdmin} isAdmin={isAdmin} />
        ))}

        {!isAdmin && (
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 11.5,
              color: TEXT_TERTIARY,
              mt: '4px',
              textAlign: 'right'
            }}
          >
            <Box component="span" sx={{ color: MEDALS.platinum.color, fontWeight: 700 }}>
              {MEDALS.platinum.label}
            </Box>{' '}
            is awarded to the top 10% of {MEDALS.gold.label} members.
          </Typography>
        )}

        {isAdmin && (
          <>
            <OutcomesTable overall={data.overall} tiers={tiers} />

            {/* The one line that tells the admin what to do — pulled out of the
              intro so it doesn't get lost mid-paragraph. */}
            <Box
              sx={{
                mt: '16px',
                p: '12px 14px',
                borderRadius: '8px',
                border: `1px solid ${BORDER_DIM}`,
                bgcolor: 'rgba(247,148,32,0.06)'
              }}
            >
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: TEXT_SECONDARY }}>
                <Box component="span" sx={{ color: BRAND_LIGHT, fontWeight: 700 }}>
                  Note:{' '}
                </Box>
                {WEAKEST_LINK_NOTE.replace(/^Note that y/, 'Y')}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
};

interface Props {
  /** 'chip' = compact stat-bar chip (default); opens the medals dialog. */
  variant?: 'chip';
  /** Live medals from the summary endpoint. Absent → the chip renders
      nothing: no placeholder numbers, ever (RDP incident, Sep 2026). */
  medals?: MedalsData;
  /** Headline treatment — taller, tinted in the tier colour and glowing, so
      the tier reads as the standout stat rather than one chip among four. */
  prominent?: boolean;
  /** Learner by default; the admin variants reframe the copy and add the
      predicted-outcomes table. */
  audience?: Audience;
}

const MedalScore: React.FC<Props> = ({ medals, prominent = false, audience = 'learner' }) => {
  const [open, setOpen] = useState(false);
  const orgFromStore = useAppSelector((s) => (s.orgDetails.data as { name?: string } | undefined)?.name);
  const orgName = (orgFromStore || getUserDetails().orgName || '').trim();
  if (!medals) return null;
  const data: MedalsData = medals;
  const overall = MEDALS[data.overall];

  return (
    <>
      <Box
        onClick={() => setOpen(true)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: prominent ? '9px' : '7px',
          height: prominent ? 38 : 32,
          px: prominent ? '16px' : '12px',
          borderRadius: TIER_RADIUS,
          // `${hex}1F` / `${hex}55` = the tier colour at ~12% / ~33% alpha.
          bgcolor: prominent ? `${overall.color}1F` : SURFACE,
          border: prominent ? `1.5px solid ${overall.color}` : `1px solid ${BORDER_DIM}`,
          boxShadow: prominent ? `0 0 14px ${overall.color}55` : 'none',
          cursor: 'pointer',
          transition: 'border-color 0.15s ease, filter 0.15s ease',
          '&:hover': { borderColor: overall.color, filter: prominent ? 'brightness(1.12)' : 'none' }
        }}
      >
        <Box
          component="span"
          sx={{
            width: prominent ? 11 : 9,
            height: prominent ? 11 : 9,
            borderRadius: '50%',
            bgcolor: overall.color
          }}
        />
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: prominent ? 13 : 11,
            fontWeight: 700,
            letterSpacing: prominent ? '1px' : '0.5px',
            color: overall.color
          }}
        >
          {overall.label.toUpperCase()}
        </Typography>
      </Box>
      <MedalsPanel open={open} onClose={() => setOpen(false)} data={data} orgName={orgName} audience={audience} />
    </>
  );
};

export default MedalScore;
